import type { ModuleSubNavItem } from "@/components/layout/moduleSubNavMatch";
import { GROUP_MANAGEMENT_PATH } from "@/lib/groups-placement";
import { CARE_PATH } from "@/lib/care-placement";

/**
 * Everyone (the people list, `Community.tsx`) moved here from `/people` (#6679) so `/people` can be
 * the Overview. Until the Overview is built, `/people` redirects here.
 */
export const PEOPLE_ALL_PATH = "/people/all";
export const PEOPLE_SETTINGS_PATH = "/people/settings/directory";
/** Engagement scoring moved here from /people/engagement-scoring (#6679); the old URL redirects. */
export const PEOPLE_ENGAGEMENT_SETTINGS_PATH = "/people/settings/engagement";

/**
 * People's module sub-nav under ui_uplift = next (decided on #7016: the spec's items that exist,
 * on today's routes). Overview joins the bar when its page is built (MODULE_MAPS.md marks it
 * "GAP → new"). Settings joined with the /people/settings/* routes (#6679).
 */
const EVERYONE: ModuleSubNavItem = { id: "everyone", label: "Everyone", href: PEOPLE_ALL_PATH, match: ["/people/", "/households/", "/family-groups/"] };
const VISITORS: ModuleSubNavItem = { id: "visitors", label: "Visitors", href: "/people/visitor-followup" };
// These two live in other areas and link across; their pages render this bar via
// CommunityLayout, so they do highlight while you're on them.
const GROUPS: ModuleSubNavItem = { id: "groups", label: "Groups", href: GROUP_MANAGEMENT_PATH };
const CARE: ModuleSubNavItem = { id: "care", label: "Care", href: CARE_PATH };
const LIFE_EVENTS: ModuleSubNavItem = { id: "life-events", label: "Life events", href: "/people/life-events" };
// Directory settings is an org-admin page, so the item shows only to an org admin.
const SETTINGS: ModuleSubNavItem = { id: "settings", label: "Settings", href: PEOPLE_SETTINGS_PATH, match: ["/people/settings/"] };

/** Pages the IA hasn't placed yet: behind "More" so the gap is visible, not hidden. */
const MORE_ITEMS: ModuleSubNavItem[] = [
  // The classic menu's entry to the merge task. The bar hides on the task itself.
  { id: "merge", label: "Merge duplicates", href: "/community/merge" },
  // Its own item, with a longer prefix than Settings' `/people/settings/`, so the page keeps its
  // own highlight. Any signed-in person can open it, so it stays here rather than behind Settings.
  { id: "engagement-scoring", label: "Engagement scoring", href: PEOPLE_ENGAGEMENT_SETTINGS_PATH },
  { id: "organizations", label: "Organizations", href: "/community/organizations" },
  { id: "registration-approvals", label: "Registration approvals", href: "/community/registration-approvals" },
  { id: "member-directory", label: "Member directory", href: "/community/directory" },
  { id: "membership-pathways", label: "Membership pathways", href: "/people/membership-pathways" },
];

export interface PeopleSubNavGates {
  /** Group management is an org-admin page (its route is behind OrgAdminRoute). */
  withGroups: boolean;
  /** Care's route only needs a signed-in member; which requests show is up to RLS. */
  withCare: boolean;
  /** Directory settings is an org-admin page (its route sits behind OrgAdminRoute). */
  withSettings?: boolean;
  /** The viewer has the People module. Without it they get only the cross-area pages below. */
  withPeople: boolean;
}

/**
 * The bar's items for a viewer. Each keeps the gate that reaches its page today, so an item the
 * viewer can't open doesn't show. `ModuleSubNav` renders nothing below two sections.
 */
export function peopleSubNavItems({ withGroups, withCare, withPeople, withSettings = false }: PeopleSubNavGates): {
  items: ModuleSubNavItem[];
  moreItems: ModuleSubNavItem[];
} {
  // No People module (an org admin without a People role, say): only the pages they can still
  // open. Groups and Care are other areas' pages, so an admin keeps both.
  if (!withPeople) {
    // An org admin with no People role can still open directory settings, so the item stays.
    return { items: [...(withGroups ? [GROUPS] : []), ...(withCare ? [CARE] : []), ...(withSettings ? [SETTINGS] : [])], moreItems: [] };
  }
  return {
    items: [EVERYONE, VISITORS, ...(withGroups ? [GROUPS] : []), ...(withCare ? [CARE] : []), LIFE_EVENTS, ...(withSettings ? [SETTINGS] : [])],
    moreItems: MORE_ITEMS,
  };
}
